import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useFirmwareFileUpload() {
  const { send, isConnected, isOpen, connect } = useWebSocketContext();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  // Convert file → Base64
  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadFirmware = useCallback(
    async ({ stationId, version, file }) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");

      try {
        // Step 1: Ensure WebSocket is connected
        if (!isConnected) {
          if (!connectionAttemptedRef.current) {
            connectionAttemptedRef.current = true;
            await connect();
          }

          // 10s timeout loop, same as your code
          await new Promise((resolve, reject) => {
            const start = Date.now();
            const interval = setInterval(() => {
              if (!mountedRef.current) {
                clearInterval(interval);
                reject(new Error("Component unmounted"));
              } else if (isOpen()) {
                clearInterval(interval);
                resolve(true);
              } else if (Date.now() - start > 10000) {
                clearInterval(interval);
                reject(new Error("WebSocket connection timeout"));
              }
            }, 100);
          });
        }

        // Step 2: Convert file to Base64
        const base64Data = await readFileAsBase64(file);

        // Step 3: Prepare WebSocket request
        const request = {
          type: "upload_firmware_url", // your backend command
          station_id: stationId,
          version,
          file_name: file.name,
          file_data: base64Data, // base64 content
        };

        console.log("Sending firmware file:", request);

        // Step 4: Send through WebSocket
        const response = await send("upload_firmware_url", request);
        console.log("Firmware Upload Response:", response);

        return { success: true, response };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, isOpen, connect, send]
  );

  // Mounted cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    uploadFirmware,
    isLoading,
    error,
  };
}
